/* eslint-disable no-underscore-dangle */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Modal,
  Popconfirm,
  Result,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
} from 'antd';
import { AxiosError } from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import PaqueryTable from '@paquery-team/lib-table';
import { useDrivers } from 'services/DriverService';
import { useTrackingRoutes } from 'services/journeys/list';
import SITELINKS from 'constants/sitelinks';
import { DEFAULT_PAGINATE } from 'constants/defaultValues';
import '../index.css';
import { useRedraft, useUnassign } from 'services/journeys';
import { Link } from 'react-router-dom';
import { EyeTwoTone } from '@ant-design/icons';
import {
  Driver,
  Journey,
  JourneyKind,
  JourneyState,
  TimeWindow,
} from 'interfaces/journey';
import { ScheduleStatus } from 'services/journeys/list';
import ProgressBar from '../components/progressBar';
import AuditLog from '../dashboard/components/AuditLog';
import { formatDuration } from 'util/Tools';
import { Option } from '..';
import useCollectionsEnabled from 'hooks/useCollectionsEnabled';
import { KindSelect, RouteSearch, kindColumn, nameColumn } from './kind';

const stateList = [
  { value: JourneyState.Assigned, name: 'Ofrecida' },
  { value: JourneyState.Offered, name: 'Ofertada' },
  { value: JourneyState.Accepted, name: 'Aceptada' },
  { value: JourneyState.Started, name: 'En ruta' },
  { value: JourneyState.Transfering, name: 'En transferencia' },
];

const scheduleStatusList: {
  value: ScheduleStatus;
  name: string;
  color?: string;
  background?: string;
}[] = [
  {
    value: 'expired',
    name: 'Vencida',
    color: '#cf1322',
    background: '#ffccc7',
  },
  {
    value: 'ongoing',
    name: 'En curso',
    color: '#237804',
    background: '#d9f7be',
  },
  {
    value: 'pending',
    name: 'Por iniciar',
    color: '#c78c00',
    background: '#fff0a3',
  },
  { value: 'none', name: 'Sin rango' },
];

const scheduleBounds = (schedule: TimeWindow) => {
  const offset = schedule.utcOffset || '+00:00';
  return {
    start: dayjs(`${schedule.date}T${schedule.from}${offset}`),
    end: dayjs(`${schedule.date}T${schedule.to}${offset}`),
  };
};

const getScheduleStatus = (schedule?: TimeWindow): ScheduleStatus => {
  if (!schedule) return 'none';
  const { start, end } = scheduleBounds(schedule);
  if (!start.isValid() || !end.isValid()) return 'none';
  const now = dayjs();
  if (now.isAfter(end)) return 'expired';
  if (now.isBefore(start)) return 'pending';
  return 'ongoing';
};

const RollbackButton = ({ journey }: { journey: Journey }) => {
  const [popconfirmOpen, setPopconfirmOpen] = useState(false);
  const { mutate: redraftRoute, isPending: isRedraftPending } = useRedraft();
  const { mutate: unassignRoute, isPending: isUnassignPending } = useUnassign();
  return (
    <Popconfirm
      destroyTooltipOnHide
      title={journey.driverName ? 'Desofrecer ruta' : 'Desofertar ruta'}
      placement="left"
      description="¿Desea volver la ruta a preparación?"
      okText="Volver a preparación"
      okButtonProps={{ danger: true }}
      cancelText="Cancelar"
      onConfirm={async () => {
        if (journey.driverName)
          unassignRoute({
            journeyId: journey.id!,
            driverId: journey.driverId!,
          });
        else redraftRoute(journey.id!);
        setPopconfirmOpen(false);
      }}
      onCancel={() => setPopconfirmOpen(false)}
      open={popconfirmOpen}
    >
      <Button
        danger
        onClick={() => {
          setPopconfirmOpen(true);
        }}
        disabled={
          isRedraftPending ||
          isUnassignPending ||
          journey.state === JourneyState.Started ||
          journey.state === JourneyState.Accepted ||
          journey.state === JourneyState.Transfering
        }
        loading={isRedraftPending || isUnassignPending}
      >
        {journey.driverName ? 'Desofrecer' : 'Desofertar'}
      </Button>
    </Popconfirm>
  );
};

const idColumn = {
  title: 'ID',
  dataIndex: 'id',
  key: 'id',
  align: 'center',
  width: '7%',
};

const progressColumn = {
  title: 'Progreso',
  key: 'progress',
  width: '16%',
  responsive: ['sm'],
  render: (journey: Journey) => (
    <Flex gap={16}>
      <Tooltip
        title={
          (journey.totalCollections ?? 0) > 0
            ? `${journey.totalPackages ?? 0} envíos y ${journey.totalCollections} colectas`
            : 'Cantidad de envíos'
        }
      >
        {(journey.totalCollections ?? 0) > 0
          ? `${(journey.totalPackages ?? 0) + (journey.totalCollections ?? 0)}`
          : (journey.totalPackages ?? '---')}
      </Tooltip>
      <ProgressBar journey={journey} />
    </Flex>
  ),
};

const statusColumn = {
  title: 'Estado',
  key: 'state',
  dataIndex: 'state',
  render: (status: string) => {
    switch (status) {
      case JourneyState.Offered:
        return (
          <Tag color="purple" style={{ margin: 0 }}>
            Ofertada
          </Tag>
        );
      case JourneyState.Assigned:
        return (
          <Tag color="yellow" style={{ margin: 0 }}>
            Ofrecida
          </Tag>
        );
      case JourneyState.Accepted:
        return (
          <Tag color="blue" style={{ margin: 0 }}>
            Aceptada
          </Tag>
        );
      case JourneyState.Started:
        return (
          <Tag color="green" style={{ margin: 0 }}>
            En ruta
          </Tag>
        );
      case JourneyState.Transfering:
        return (
          <Tag color="cyan" style={{ margin: 0 }}>
            En transferencia
          </Tag>
        );
      default:
        return 'Desconocido';
    }
  },
  responsive: ['md'],
  width: '9%',
  align: 'center',
};

const scheduleColumn = {
  title: 'Rango horario',
  key: 'schedule',
  align: 'center',
  responsive: ['md'],
  render: (journey: Journey) => {
    const status = getScheduleStatus(journey.schedule);
    if (status === 'none' || !journey.schedule) return '-';
    const { start, end } = scheduleBounds(journey.schedule);
    const option = scheduleStatusList.find((s) => s.value === status);
    return (
      <Tooltip title={option?.name}>
        <Tag
          bordered={false}
          style={{
            margin: 0,
            color: option?.color,
            background: option?.background,
          }}
        >
          {`${start.tz().format('HH:mm')} - ${end.tz().format('HH:mm')}`}
        </Tag>
      </Tooltip>
    );
  },
};

const timeColumn = {
  title: 'Tiempo estimado',
  dataIndex: 'estimatedTime',
  render: (time: number) => formatDuration(time),
  key: 'time',
  responsive: ['md'],
};

const distanceColumn = {
  title: 'Distancia total',
  dataIndex: 'estimatedDistance',
  render: (distance: number) =>
    distance ? `${(distance / 1000).toFixed(2)} km` : 'Desconocido',
  key: 'distance',
  responsive: ['lg'],
};

// TODO change to driver name + unassign button
const driverNameColumn = {
  title: 'Driver',
  key: 'paquerName',
  dataIndex: 'driverName',
  render: (driverName: string) => driverName || 'Sin asignar',
  width: '10%',
  ellipsis: true,
  responsive: ['sm'],
};

// Rótulo y fecha del último cambio de estado grande. En transferencia se sigue mostrando
// "Iniciada" (mismo startedDate): no hay una fecha propia de esa transición.
const getLastChangeLabel = (
  journey: Journey,
): { label: string; date?: Date } => {
  switch (journey.state) {
    case JourneyState.Offered:
      return { label: 'Ofertada', date: journey.offeredDate };
    case JourneyState.Assigned:
      return { label: 'Ofrecida', date: journey.offeredDate };
    case JourneyState.Accepted:
      return { label: 'Aceptada', date: journey.acceptedDate };
    case JourneyState.Started:
    case JourneyState.Transfering:
      return { label: 'Iniciada', date: journey.startedDate };
    default:
      return { label: 'Creada', date: journey.creationDate };
  }
};

const lastChangeColumn = {
  title: 'Última actualización',
  key: 'lastChange',
  render: (journey: Journey) => {
    const { label, date } = getLastChangeLabel(journey);
    // ponytail: journeys ya en curso antes de este deploy todavía no tienen offeredDate/
    // acceptedDate propios; se cae a creationDate en vez de mostrar "Desconocido".
    const resolvedDate = date ?? journey.creationDate;
    if (!resolvedDate) return 'Desconocido';
    const elapsedSeconds = dayjs().diff(dayjs(resolvedDate), 'seconds');
    const resolvedLabel = date ? label : 'Creada';
    return `${resolvedLabel} hace ${formatDuration(elapsedSeconds)}`;
  },
  responsive: ['sm'],
};

const actionsColumn = {
  key: 'detail',
  render: (journey: Journey) => (
    <Flex align="center" justify="space-around">
      <Tooltip title="Detalle">
        <Link to={`${SITELINKS.journey}/${journey.id}`}>
          <EyeTwoTone style={{ fontSize: 18 }} />
        </Link>
      </Tooltip>
      <AuditLog mode="list" journeyId={journey.id} />
      <RollbackButton journey={journey} />
    </Flex>
  ),
  width: '15%',
  align: 'center',
};

const dataColumnsFor = (withKind: boolean) => [
  idColumn,
  nameColumn,
  ...(withKind ? [kindColumn] : []),
  progressColumn,
  statusColumn,
  scheduleColumn,
  timeColumn,
  distanceColumn,
  driverNameColumn,
  lastChangeColumn,
  actionsColumn,
];


const RoutesTracking = () => {
  const collectionsEnabled = useCollectionsEnabled();
  const dataColumns = dataColumnsFor(collectionsEnabled);
  const { data: drivers } = useDrivers();
  const [driverList, setDriverList] = useState<Option[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<number>();
  const [selectedKind, setSelectedKind] = useState<JourneyKind>();
  const [selectedState, setSelectedState] = useState<JourneyState>();
  const [selectedScheduleStatus, setSelectedScheduleStatus] =
    useState<ScheduleStatus>();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [date, setDate] = useState<Dayjs>();
  const [search, setSearch] = useState<string>();
  const queries = useMemo(
    () => ({
      driverId: selectedDriver,
      kind: selectedKind,
      date,
      page,
      size,
      state: selectedState ? [selectedState] : [],
      search,
      scheduleStatus: selectedScheduleStatus,
    }),
    [
      selectedDriver,
      selectedKind,
      date,
      page,
      size,
      selectedState,
      search,
      selectedScheduleStatus,
    ],
  );
  const { data, isLoading, isFetching, error, refetch } =
    useTrackingRoutes(queries);

  const pageable = useMemo(() => {
    return {
      // ponytail: el control lo maneja el estado local `page`; del server solo el total
      pageNumber: page,
      pageSize: size ?? DEFAULT_PAGINATE.pageSize,
      totalElements: data?.total_elements ?? DEFAULT_PAGINATE.totalElements,
    };
  }, [page, size, data]);

  const handleOnPaginate = (pagination: {
    pageNumber: number;
    pageSize: number;
  }) => {
    setPage(pagination.pageNumber);
    setSize(pagination.pageSize);
  };

  // ponytail: identidad estable, si no el effect del Searcher (allowEmptySearch)
  // dispara onSearching('') en cada render y resetea la página
  const handleSearch = useCallback((value?: string) => {
    setSearch(value || undefined);
    setPage(0);
  }, []);

  useEffect(() => {
    const driversOptions: Option[] = [
      {
        value: 0,
        name: 'Sin driver',
      },
    ];
    if (drivers && drivers.length && drivers.length > 0) {
      setDriverList(
        drivers.forEach(
          (driver: { id: number; name: string; lastName: string }) =>
            driversOptions.push({
              value: driver.id,
              name: `${driver.name} ${driver.lastName}`,
            }),
        ),
      );
    }
    setDriverList(driversOptions);
  }, [drivers]);

  const extraButtons = [
    <Button type="primary" onClick={() => refetch()}>
      Reintentar
    </Button>,
  ];

  const getResultStatus = (err: AxiosError) => {
    if (!err.response?.status) return 'error';
    if (
      err.response.status === 404 ||
      err.response.status === 403 ||
      err.response.status === 500
    )
      return err.response.status;
    return 'error';
  };

  return (
    <Row gutter={[16, 36]}>
      <Col xs={24}>
        {error ? (
          <Result
            status={getResultStatus(error as AxiosError)}
            title="Algo salio mal..."
            subTitle={error.message}
            extra={extraButtons}
          />
        ) : (
          <PaqueryTable
            header={{
              extraMainAction: (
                <Space>
                  <RouteSearch onSearch={handleSearch} />
                  {collectionsEnabled && (
                    <KindSelect onChange={setSelectedKind} />
                  )}
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Driver"
                    style={{ width: 200 }}
                    options={driverList.map((o) => ({
                      value: o.value,
                      label: o.name,
                    }))}
                    onChange={(value: number) => setSelectedDriver(value)}
                  />
                  <Select
                    allowClear
                    placeholder="Estado"
                    style={{ width: 160 }}
                    options={stateList.map((o) => ({
                      value: o.value,
                      label: o.name,
                    }))}
                    onChange={(value: JourneyState) => setSelectedState(value)}
                  />
                  <Select
                    allowClear
                    placeholder="Rango horario"
                    style={{ width: 160 }}
                    options={scheduleStatusList.map((o) => ({
                      value: o.value,
                      label: o.name,
                    }))}
                    onChange={(value: ScheduleStatus) => {
                      setSelectedScheduleStatus(value);
                      setPage(0);
                    }}
                  />
                  <DatePicker
                    placeholder="Seleccione fecha de creación"
                    style={{ width: 220 }}
                    onChange={(date: Dayjs) => setDate(date ?? undefined)}
                  />
                </Space>
              ),
              refresh: refetch,
              title: 'Seguimiento',
            }}
            onChangePaginate={handleOnPaginate}
            loading={isLoading}
            fetching={isFetching}
            dataSource={
              data && data.journeys?.map((j) => ({ ...j, id: j.journeyId }))
            }
            dataColumns={dataColumns}
            paginate={pageable}
            colsForSmallDevice={dataColumns}
            colsForMediumDevice={dataColumns}
            colsForLargeDevice={dataColumns}
          />
        )}
      </Col>
    </Row>
  );
};

export default RoutesTracking;
